import { useQuery } from "@tanstack/react-query";
import { USER_API } from "config";
import { normalizeId } from "utils";
import { budgetKeys } from "queries";
import { useAxios } from "../useAxios";
import { unwrapBudgetApiData } from "./budgetCachePatch";

export interface BudgetVersionDiffLine {
  key: string;
  account_number: string | null;
  name: string;
  status: string;
  before_estimate: number | null;
  after_estimate: number | null;
  field_changes?: Array<{
    field: string;
    before: string | number | null;
    after: string | number | null;
  }>;
}

export interface BudgetVersionDiffCategoryDetail {
  category_name: string;
  line_changes: BudgetVersionDiffLine[];
}

interface UseBudgetVersionDiffCategoryParams {
  projectId: string | number;
  documentId: string | number;
  budgetId?: string;
  categoryName?: string | null;
  againstBudgetId?: number;
  enabled?: boolean;
}

export const useBudgetVersionDiffCategory = ({
  projectId,
  documentId,
  budgetId,
  categoryName,
  againstBudgetId,
  enabled = true,
}: UseBudgetVersionDiffCategoryParams) => {
  const axios = useAxios({ allResponse: false });
  const pid = normalizeId(projectId);
  const did = normalizeId(documentId);

  return useQuery({
    queryKey: [
      ...budgetKeys.all,
      "version-diff-category",
      pid,
      did,
      budgetId,
      categoryName,
      againstBudgetId ?? "primary",
    ],
    queryFn: async (): Promise<BudgetVersionDiffCategoryDetail> => {
      if (!budgetId || !categoryName) throw new Error("Budget ID and category required");
      const params = againstBudgetId ? { against: againstBudgetId } : undefined;
      const response = await axios.get<
        BudgetVersionDiffCategoryDetail | { data: BudgetVersionDiffCategoryDetail }
      >(USER_API.budgetVersionDiffCategory(pid, did, budgetId, categoryName), { params });
      return unwrapBudgetApiData<BudgetVersionDiffCategoryDetail>(
        response as unknown as
          | BudgetVersionDiffCategoryDetail
          | { data: BudgetVersionDiffCategoryDetail }
      );
    },
    enabled: enabled && Boolean(budgetId) && Boolean(categoryName),
  });
};
